import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyDashboardPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [t, p] = await Promise.all([
          axios.get(`${API_URL}/api/my/tasks`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/my/profile`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setTasks(Array.isArray(t.data) ? t.data : []);
        setProfile(p.data || null);
      } catch (e) {
        setErr(e.message || 'Failed to load dashboard');
      }
    })();
  }, []);

  if (err) {
    return (
      <div className="p-6">
        <div className="bg-rose-50 border border-rose-200 rounded p-4 text-sm text-rose-800">
          <strong>Dashboard error:</strong> {err}
        </div>
      </div>
    );
  }

  const open = tasks.filter((t) => t.status !== 'completed').length;
  const inProg = tasks.filter((t) => t.status === 'in_progress').length;
  const blocked = tasks.filter((t) => t.status === 'blocked' || t.status === 'client_dependency').length;
  const completed = tasks.filter((t) => t.status === 'completed').length;
  const dueSoon = tasks.filter((t) => {
    if (!t.due_date || t.status === 'completed') return false;
    const d = new Date(t.due_date); const today = new Date(); today.setHours(0, 0, 0, 0);
    return (d - today) / 86400000 <= 3;
  });

  return (
    <div className="space-y-4 p-6" data-testid="my-dashboard">
      <Card className="p-5 bg-gradient-to-br from-blue-50 to-white">
        <div className="text-xs uppercase tracking-wide text-gray-500">Welcome back</div>
        <div className="text-2xl font-bold mt-1" style={{ color: '#1a2e4a' }}>
          {profile?.name || user?.name || 'Hi there'} 👋
        </div>
        {profile?.employee?.designation_id && (
          <div className="text-xs text-gray-600 mt-1">{profile?.employee?.designation_id}</div>
        )}
        <div className="text-xs text-gray-500 mt-1">
          {profile?.is_team_lead && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-[10px] font-bold mr-2">TEAM LEAD</span>}
          Role: <strong className="capitalize">{user?.role}</strong>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">Open</div><div className="text-2xl font-bold text-blue-700">{open}</div></Card>
        <Card className="p-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">In Progress</div><div className="text-2xl font-bold text-emerald-700">{inProg}</div></Card>
        <Card className="p-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">Blocked</div><div className="text-2xl font-bold text-rose-700">{blocked}</div></Card>
        <Card className="p-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">Completed</div><div className="text-2xl font-bold text-gray-700">{completed}</div></Card>
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-bold" style={{ color: '#1a2e4a' }}>📋 My Open Tasks</div>
          <Link to="/my/tasks" className="text-xs text-blue-700 underline">Go to My Tasks →</Link>
        </div>
        {tasks.filter((t) => t.status !== 'completed').slice(0, 6).map((t) => (
          <div key={t.id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-xs">
            <span className="truncate flex-1">{t.title}</span>
            <span className="text-gray-500 mr-2 capitalize">{(t.status || 'pending').replace(/_/g, ' ')}</span>
            <span className="text-gray-400">{t.due_date || ''}</span>
          </div>
        ))}
        {tasks.length === 0 && <div className="text-xs text-gray-400 italic">No tasks assigned.</div>}
      </Card>

      {dueSoon.length > 0 && (
        <Card className="p-4 border-amber-300 bg-amber-50">
          <div className="text-sm font-bold text-amber-800 mb-2">⏰ Due in 3 days</div>
          {dueSoon.map((t) => (
            <div key={t.id} className="text-xs py-0.5">📅 {t.due_date} — {t.title}</div>
          ))}
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link to="/my/attendance" className="block"><Card className="p-3 hover:bg-gray-50"><div className="text-xs font-bold">🕐 Mark Attendance</div><div className="text-[10px] text-gray-500 mt-0.5">Check-in / out</div></Card></Link>
        <Link to="/my/leaves" className="block"><Card className="p-3 hover:bg-gray-50"><div className="text-xs font-bold">🌴 Request Leave</div><div className="text-[10px] text-gray-500 mt-0.5">Apply for time off</div></Card></Link>
        <Link to="/permission-request" target="_blank" rel="noreferrer" className="block" data-testid="dash-permission-link"><Card className="p-3 hover:bg-gray-50 border-amber-200"><div className="text-xs font-bold text-amber-800">⏰ Request Permission</div><div className="text-[10px] text-gray-500 mt-0.5">Late arrival / early leave</div></Card></Link>
        <Link to="/standup" className="block"><Card className="p-3 hover:bg-gray-50"><div className="text-xs font-bold">📅 Daily Standup</div><div className="text-[10px] text-gray-500 mt-0.5">Post today's updates</div></Card></Link>
      </div>
    </div>
  );
}
